import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTheme } from '../hooks/useTheme.ts';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReportType {
  key: string;
  label: string;
  description: string;
}

interface Recipient {
  email: string;
}

interface ReportRun {
  id: number;
  pad_id: number | null;
  run_at_ms: number;
  trigger_source: string;
  status: 'queued' | 'success' | 'error' | 'skipped';
  error: string | null;
  recipients_sent: string[];
}

interface ReportDefinition {
  id: number;
  organization_id: number;
  organization_name?: string;
  pad_id: number | null;
  pad_name?: string | null;
  report_type: string;
  name: string;
  params: Record<string, unknown>;
  schedule: 'daily' | 'manual';
  send_hour_local: number;
  send_minute_local: number;
  enabled: boolean;
  recipients: string[];
}

interface ReportsManagementModalProps {
  show: boolean;
  onHide: () => void;
  padId?: number;
  userRole?: string;
  /** Optional theme override from the parent (e.g. TreatmentDataGraph passes
   *  its own `theme` string). Falls back to the useTheme() hook when omitted. */
  theme?: string;
}

const TIMEZONES = [
  { value: 'Central Standard Time', label: 'Central (CT)' },
  { value: 'Mountain Standard Time', label: 'Mountain (MT)' },
  { value: 'Eastern Standard Time', label: 'Eastern (ET)' },
  { value: 'Pacific Standard Time', label: 'Pacific (PT)' },
  { value: 'UTC', label: 'UTC' },
];

// ─── Component ────────────────────────────────────────────────────────────────

const ReportsManagementModal: React.FC<ReportsManagementModalProps> = ({
  show, onHide, padId, userRole = 'Viewer', theme: themeProp,
}) => {
  const { theme: hookTheme } = useTheme();
  const theme = themeProp ?? hookTheme;   // parent-provided theme wins
  const isDark = theme === 'dark';
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api')
    .replace(/\/api$/, '');
  const canEdit = userRole !== 'Viewer';

  const [loading, setLoading] = useState(false);
  const [orgId, setOrgId] = useState<number | null>(null);
  const [isProfrac, setIsProfrac] = useState(false);
  const [reports, setReports] = useState<ReportDefinition[]>([]);
  const [reportTypes, setReportTypes] = useState<ReportType[]>([]);
  const [padName, setPadName] = useState<string>('');

  // editing state — null = list view, otherwise editing/creating
  const [editing, setEditing] = useState<ReportDefinition | null>(null);
  const [recipientInput, setRecipientInput] = useState('');
  const [runs, setRuns] = useState<Record<number, ReportRun[]>>({});
  const [queuingId, setQueuingId] = useState<number | null>(null);

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  // ─── Data loading ───────────────────────────────────────────────────────────

  const loadContext = useCallback(async () => {
    if (!padId) return;
    setLoading(true);
    try {
      // pad tells us the owning org; also grab the display name
      const padRes = await axios.get(`${backendUrl}/pads/${padId}`, authHeader());
      const pad = padRes.data;
      setOrgId(pad.organization_id);
      setPadName(pad.pad_name || `Pad ${padId}`);

      // report type catalog for the template picker
      const typesRes = await axios.get(`${backendUrl}/report-types`, authHeader());
      setReportTypes(typesRes.data || []);

      // Show only reports relevant to THIS pad:
      //   • organization-wide reports (pad_id null) — they run on any pad, and
      //   • pad-scoped reports whose pad_id is exactly this pad.
      // Reports scoped to OTHER pads are hidden here (open the modal from that
      // pad to manage them). We fetch org-scoped so cross-org reports never leak.
      const listRes = await axios.get(
        `${backendUrl}/organizations/${pad.organization_id}/reports`, authHeader());
      const orgReports: ReportDefinition[] = listRes.data || [];
      const relevant = orgReports.filter(
        r => r.pad_id == null || r.pad_id === padId);
      setReports(relevant);
    } catch (err) {
      console.error('Failed to load reports context', err);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [padId, backendUrl]);

  useEffect(() => {
    if (show) {
      setEditing(null);
      loadContext();
    }
  }, [show, loadContext]);

  const loadRuns = async (reportId: number) => {
    try {
      const res = await axios.get(
        `${backendUrl}/reports/${reportId}/runs?limit=10`, authHeader());
      setRuns(prev => ({ ...prev, [reportId]: res.data || [] }));
    } catch {
      /* non-fatal */
    }
  };

  // ─── Editor helpers ───────────────────────────────────────────────────────────

  const startNew = () => {
    setEditing({
      id: 0,
      organization_id: orgId ?? 0,
      pad_id: padId ?? null,
      report_type: reportTypes[0]?.key || 'momentum_daily_v1',
      name: '',
      params: { report_timezone: 'Central Standard Time' },
      schedule: 'daily',
      send_hour_local: 6,
      send_minute_local: 0,
      enabled: true,
      recipients: [],
    });
    setRecipientInput('');
  };

  const startEdit = (r: ReportDefinition) => {
    setEditing({ ...r, recipients: [...r.recipients] });
    setRecipientInput('');
  };

  const addRecipient = () => {
    const email = recipientInput.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.warning('Enter a valid email');
      return;
    }
    if (!editing) return;
    if (editing.recipients.includes(email)) {
      toast.info('Recipient already added');
      return;
    }
    setEditing({ ...editing, recipients: [...editing.recipients, email] });
    setRecipientInput('');
  };

  const removeRecipient = (email: string) => {
    if (!editing) return;
    setEditing({ ...editing, recipients: editing.recipients.filter(e => e !== email) });
  };

  const saveReport = async () => {
    if (!editing) return;
    if (!editing.name.trim()) return void toast.warning('Report name is required');
    if (editing.recipients.length === 0) return void toast.warning('Add at least one recipient');
    const targetOrg = editing.organization_id || orgId;
    if (!targetOrg) return void toast.error('No organization resolved for this report');

    setLoading(true);
    try {
      const payload = {
        id: editing.id || undefined,
        report_type: editing.report_type,
        name: editing.name.trim(),
        pad_id: editing.pad_id,          // null = org-wide
        params: editing.params,
        schedule: editing.schedule,
        send_hour_local: editing.send_hour_local,
        send_minute_local: editing.send_minute_local,
        enabled: editing.enabled,
        recipients: editing.recipients,
      };
      await axios.post(
        `${backendUrl}/organizations/${targetOrg}/reports`, payload, authHeader());
      toast.success('Report saved');
      setEditing(null);
      await loadContext();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) && err.response?.data?.message
        ? err.response.data.message : 'Failed to save report';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const deleteReport = async (r: ReportDefinition) => {
    if (!window.confirm(`Delete report "${r.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${backendUrl}/reports/${r.id}`, authHeader());
      toast.success('Report deleted');
      await loadContext();
    } catch {
      toast.error('Failed to delete report');
    }
  };

  const toggleReport = async (r: ReportDefinition) => {
    try {
      const res = await axios.patch(
        `${backendUrl}/reports/${r.id}/toggle`, {}, authHeader());
      setReports(prev => prev.map(x =>
        x.id === r.id ? { ...x, enabled: res.data.enabled } : x));
    } catch {
      toast.error('Failed to toggle report');
    }
  };

  const sendNow = async (r: ReportDefinition) => {
    const targetPad = r.pad_id ?? padId;
    if (!targetPad) {
      toast.warning('Org-wide reports can only be sent by the scheduler');
      return;
    }
    setQueuingId(r.id);
    try {
      await axios.post(
        `${backendUrl}/reports/${r.id}/queue`, { pad_id: targetPad }, authHeader());
      toast.success('Report queued — the worker will send it shortly');
      setTimeout(() => loadRuns(r.id), 1500);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) && err.response?.data?.message
        ? err.response.data.message : 'Failed to queue report';
      toast.error(msg);
    } finally {
      setQueuingId(null);
    }
  };

  // ─── Styling ──────────────────────────────────────────────────────────────────

  // Palette matched to the rest of the app (see TreatmentDataGraph.tsx):
  //   surfaces #303335, borders #5C646B, text #e5e5e5, dim #8b9cb7,
  //   brand gold #F9C430, danger #FF5F4D.
  const bg       = isDark ? '#26282a' : '#ffffff';   // modal body (a touch darker than cards)
  const cardBg   = isDark ? '#303335' : '#f7f9fb';   // report cards / inputs
  const cardBg2  = isDark ? '#3a3d40' : '#eef1f4';   // hover / nested
  const border   = isDark ? '#5C646B' : '#e2e8f0';
  const textColor = isDark ? '#e5e5e5' : '#1a202c';
  const dimText  = isDark ? '#8b9cb7' : '#64748b';
  const accent   = '#F9C430';                        // brand gold (matches app)

  const inputStyle: React.CSSProperties = {
    backgroundColor: isDark ? '#26282a' : '#ffffff',
    color: textColor,
    border: `1px solid ${border}`,
  };

  const statusColor = (s: string) => ({
    success: '#4ADE80', error: '#FF5F4D', queued: '#F9C430', skipped: '#8b9cb7',
  }[s] || dimText);

  const selectStyles = {
    control: (base: Record<string, unknown>) => ({
      ...base, backgroundColor: inputStyle.backgroundColor, borderColor: border,
    }),
    menu: (base: Record<string, unknown>) => ({ ...base, backgroundColor: cardBg, zIndex: 9999 }),
    singleValue: (base: Record<string, unknown>) => ({ ...base, color: textColor }),
    option: (base: Record<string, unknown>, state: { isFocused: boolean }) => ({
      ...base,
      backgroundColor: state.isFocused ? accent : cardBg,
      color: state.isFocused ? '#1a1a1a' : textColor,   // dark text on gold hover
    }),
  };

  // ─── Render: editor ────────────────────────────────────────────────────────────

  const LOOKBACK_OPTIONS = [
    { value: 0, label: 'All data (no window)' },
    { value: 24, label: 'Last 24 hours' },
    { value: 48, label: 'Last 48 hours' },
    { value: 72, label: 'Last 72 hours' },
    { value: 168, label: 'Last 7 days' },
  ];

  const renderEditor = () => {
    if (!editing) return null;
    const tz = (editing.params?.report_timezone as string) || 'Central Standard Time';
    const lookback = (editing.params?.lookback_hours as number | null) ?? 0;
    return (
      <div>
        <h5 style={{ color: textColor }}>
          {editing.id ? 'Edit Report' : 'New Report'}
        </h5>

        <Form.Group className="mb-3">
          <Form.Label style={{ color: dimText }}>Template</Form.Label>
          <Select
            styles={selectStyles}
            isDisabled={!canEdit || !!editing.id}
            value={reportTypes
              .map(t => ({ value: t.key, label: t.label }))
              .find(o => o.value === editing.report_type) || null}
            options={reportTypes.map(t => ({ value: t.key, label: t.label }))}
            onChange={(opt) => opt && setEditing({ ...editing, report_type: opt.value })}
          />
          <small style={{ color: dimText }}>
            {reportTypes.find(t => t.key === editing.report_type)?.description}
          </small>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label style={{ color: dimText }}>Report Name</Form.Label>
          <Form.Control
            style={inputStyle}
            disabled={!canEdit}
            value={editing.name}
            placeholder="e.g. ConocoPhillips Momentum Daily"
            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
          />
        </Form.Group>

        <div className="d-flex gap-3 mb-3">
          <Form.Group style={{ flex: 1 }}>
            <Form.Label style={{ color: dimText }}>Frequency</Form.Label>
            <Form.Select
              style={inputStyle}
              disabled={!canEdit}
              value={editing.schedule}
              onChange={(e) => setEditing({
                ...editing, schedule: e.target.value as 'daily' | 'manual' })}
            >
              <option value="daily">Daily (scheduled)</option>
              <option value="manual">Manual only</option>
            </Form.Select>
          </Form.Group>

          <Form.Group style={{ flex: 1 }}>
            <Form.Label style={{ color: dimText }}>Send Time (local)</Form.Label>
            <Form.Control
              type="time"
              style={inputStyle}
              disabled={!canEdit || editing.schedule !== 'daily'}
              value={`${String(editing.send_hour_local).padStart(2, '0')}:${String(editing.send_minute_local).padStart(2, '0')}`}
              onChange={(e) => {
                const [h, m] = e.target.value.split(':');
                setEditing({
                  ...editing,
                  send_hour_local: parseInt(h || '0', 10),
                  send_minute_local: parseInt(m || '0', 10),
                });
              }}
            />
          </Form.Group>

          <Form.Group style={{ flex: 1 }}>
            <Form.Label style={{ color: dimText }}>Timezone</Form.Label>
            <Select
              styles={selectStyles}
              isDisabled={!canEdit}
              value={TIMEZONES.find(o => o.value === tz)}
              options={TIMEZONES}
              onChange={(opt) => opt && setEditing({
                ...editing,
                params: { ...editing.params, report_timezone: opt.value },
              })}
            />
          </Form.Group>
        </div>

        <div className="d-flex gap-3 mb-3">
          <Form.Group style={{ flex: 1 }}>
            <Form.Label style={{ color: dimText }}>Data Window</Form.Label>
            <Select
              styles={selectStyles}
              isDisabled={!canEdit}
              value={LOOKBACK_OPTIONS.find(o => o.value === lookback) || LOOKBACK_OPTIONS[0]}
              options={LOOKBACK_OPTIONS}
              onChange={(opt) => {
                if (!opt) return;
                const next = { ...editing.params } as Record<string, unknown>;
                if (opt.value === 0) {
                  delete next.lookback_hours;   // all-time = omit the param
                } else {
                  next.lookback_hours = opt.value;
                }
                setEditing({ ...editing, params: next });
              }}
            />
            <small style={{ color: dimText }}>
              Only include data from this window before each send (e.g. a daily
              report showing just the last 24h of new activity).
            </small>
          </Form.Group>
        </div>

        <Form.Group className="mb-3">
          <Form.Label style={{ color: dimText }}>Scope</Form.Label>
          {(() => {
            // A pad-scoped report can only ever be scoped to its OWN pad. Since
            // this modal only surfaces org-wide reports and reports for THIS
            // pad, toggling to "pad-specific" always means this pad — never a
            // reassignment of someone else's report.
            const scopedToThisPad = editing.pad_id != null && editing.pad_id === padId;
            const scopedElsewhere = editing.pad_id != null && editing.pad_id !== padId;
            // Show the pad the report actually belongs to, not the pad the
            // modal was opened from.
            const scopePadName = scopedElsewhere
              ? (editing.pad_name || `Pad ${editing.pad_id}`)
              : padName;
            return (
              <>
                <Form.Check
                  type="switch"
                  id="scope-switch"
                  disabled={!canEdit || scopedElsewhere}
                  label={editing.pad_id != null
                    ? `Pad-specific (${scopePadName})`
                    : 'Organization-wide (all active pads)'}
                  checked={editing.pad_id != null}
                  onChange={(e) => setEditing({
                    ...editing, pad_id: e.target.checked ? (padId ?? null) : null })}
                  style={{ color: textColor }}
                />
                <small style={{ color: dimText }}>
                  {scopedElsewhere
                    ? `This report is scoped to ${scopePadName} — open it from that pad to change its scope.`
                    : editing.pad_id != null
                      ? 'Sends only for this pad.'
                      : 'Sends for every active pad in the organization.'}
                </small>
              </>
            );
          })()}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label style={{ color: dimText }}>Recipients</Form.Label>
          <div className="d-flex gap-2 mb-2">
            <Form.Control
              style={inputStyle}
              disabled={!canEdit}
              value={recipientInput}
              placeholder="name@company.com"
              onChange={(e) => setRecipientInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
            />
            <Button variant="outline-secondary" disabled={!canEdit} onClick={addRecipient}
              style={{ color: textColor, borderColor: border }}>
              Add
            </Button>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {editing.recipients.map(email => (
              <span key={email} style={{
                backgroundColor: cardBg, border: `1px solid ${border}`,
                borderRadius: 12, padding: '2px 10px', color: textColor, fontSize: 13,
              }}>
                {email}
                {canEdit && (
                  <span
                    style={{ marginLeft: 8, cursor: 'pointer', color: '#FF5F4D' }}
                    onClick={() => removeRecipient(email)}
                  >×</span>
                )}
              </span>
            ))}
            {editing.recipients.length === 0 && (
              <small style={{ color: dimText }}>No recipients yet.</small>
            )}
          </div>
        </Form.Group>

        <div className="d-flex justify-content-between mt-4">
          <Button variant="outline-secondary" onClick={() => setEditing(null)}
            style={{ color: textColor, borderColor: border }}>
            Cancel
          </Button>
          {canEdit && (
            <Button
              style={{ backgroundColor: accent, borderColor: accent,
                       color: '#1a1a1a', fontWeight: 600 }}
              onClick={saveReport}
              disabled={loading}
            >
              {loading ? <Spinner size="sm" /> : 'Save Report'}
            </Button>
          )}
        </div>
      </div>
    );
  };

  // ─── Render: list ──────────────────────────────────────────────────────────────

  const renderList = () => (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span style={{ color: dimText }}>
          {isProfrac
            ? 'Showing reports across all organizations (ProFrac).'
            : `Reports for this organization.`}
        </span>
        {canEdit && (
          <Button
            style={{ backgroundColor: accent, borderColor: accent,
                     color: '#1a1a1a', fontWeight: 600 }}
            onClick={startNew}
            disabled={!orgId}
          >
            + New Report
          </Button>
        )}
      </div>

      {loading && <div className="text-center py-4"><Spinner /></div>}

      {!loading && reports.length === 0 && (
        <div style={{ color: dimText }} className="text-center py-4">
          No reports configured yet.
        </div>
      )}

      {!loading && reports.map(r => (
        <div key={r.id} style={{
          backgroundColor: cardBg, border: `1px solid ${border}`,
          borderRadius: 8, padding: '12px 16px', marginBottom: 10,
        }}>
          <div className="d-flex justify-content-between align-items-start">
            <div style={{ flex: 1 }}>
              <div style={{ color: textColor, fontWeight: 600 }}>
                {r.name}
                {!r.enabled && (
                  <span style={{
                    marginLeft: 8, fontSize: 11, color: '#f5b942',
                    border: '1px solid #f5b942', borderRadius: 8, padding: '1px 6px',
                  }}>Disabled</span>
                )}
              </div>
              <div style={{ color: dimText, fontSize: 13, marginTop: 2 }}>
                {isProfrac && r.organization_name ? `${r.organization_name} · ` : ''}
                {reportTypes.find(t => t.key === r.report_type)?.label || r.report_type}
                {' · '}
                {r.schedule === 'daily'
                  ? `Daily @ ${String(r.send_hour_local).padStart(2, '0')}:${String(r.send_minute_local).padStart(2, '0')}`
                  : 'Manual'}
                {' · '}
                {r.pad_id
                  ? (r.pad_name ? `Pad: ${r.pad_name}` : 'Pad-specific')
                  : 'Org-wide'}
                {' · '}
                {(() => {
                  const lb = (r.params?.lookback_hours as number | undefined);
                  if (!lb) return 'All data';
                  if (lb % 24 === 0) return `Last ${lb / 24}d`;
                  return `Last ${lb}h`;
                })()}
                {' · '}
                {r.recipients.length} recipient{r.recipients.length === 1 ? '' : 's'}
              </div>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <Button
                size="sm"
                style={{ color: accent, borderColor: accent,
                         backgroundColor: 'transparent' }}
                disabled={queuingId === r.id}
                onClick={() => sendNow(r)}
              >
                {queuingId === r.id ? <Spinner size="sm" /> : 'Send Now'}
              </Button>
              {canEdit && (
                <>
                  <Button size="sm" variant="outline-secondary" onClick={() => startEdit(r)}
                    style={{ color: textColor, borderColor: border }}>
                    Edit
                  </Button>
                  <Form.Check
                    type="switch"
                    id={`toggle-${r.id}`}
                    checked={r.enabled}
                    onChange={() => toggleReport(r)}
                  />
                  <Button size="sm" variant="outline-danger" onClick={() => deleteReport(r)}
                    style={{ color: '#FF5F4D', borderColor: '#FF5F4D' }}>
                    Delete
                  </Button>
                </>
              )}
            </div>
          </div>

          {runs[r.id] && runs[r.id].length > 0 && (
            <div style={{ marginTop: 8, borderTop: `1px solid ${border}`, paddingTop: 6 }}>
              {runs[r.id].slice(0, 3).map(run => (
                <div key={run.id} style={{ fontSize: 12, color: dimText }}>
                  <span style={{ color: statusColor(run.status), fontWeight: 600 }}>
                    {run.status.toUpperCase()}
                  </span>
                  {' · '}
                  {new Date(run.run_at_ms).toLocaleString()}
                  {run.error ? ` · ${run.error}` : ''}
                </div>
              ))}
            </div>
          )}
          <div
            style={{ fontSize: 12, color: accent, cursor: 'pointer', marginTop: 4 }}
            onClick={() => loadRuns(r.id)}
          >
            View recent runs
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} centered size="lg" contentClassName="border-0">
      <Modal.Header
        closeButton
        closeVariant={isDark ? 'white' : undefined}
        style={{ backgroundColor: bg, borderBottom: `2px solid ${accent}` }}
      >
        <Modal.Title style={{ color: textColor, fontSize: 18 }}>
          Reports {padName ? <span style={{ color: accent }}>— {padName}</span> : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: bg, maxHeight: '70vh', overflowY: 'auto' }}>
        {editing ? renderEditor() : renderList()}
      </Modal.Body>
    </Modal>
  );
};

export default ReportsManagementModal;